/**
 * THE LINE ON THE WATER.
 *
 * -- WHAT THIS FILE IS, AND WHAT IT REFUSES TO BE ---------------------------------------------
 *
 * Reelin's one verb happens on the water and until this file nothing showed it: the boat sat
 * on the sea and a catch mark blinked beside it. This is the line, the bobber, the bite ring
 * and the pull, as a PURE FUNCTION of the catch phase Task 3's `catchPresentationAt` decides,
 * the milliseconds spent in that phase, the water's loop frame and the reduced-motion word.
 * It never reads a clock, a save or a session, and it never judges whether a fish came aboard:
 * `detectCatch` is the only detector and this file only draws the phases it names.
 *
 * -- EVERYTHING IS FROM THE BOAT --------------------------------------------------------------
 *
 * Every offset is in screen art px FROM THE BOAT'S OWN CENTRE, never from the frame's. Those
 * were the same point until the arrival camera was allowed to swing the frame onto Murrin
 * (interpolate.ts "THE FRAME ON ARRIVAL"); an offset from the centre would leave the line out
 * on open water a hundred pixels off the hull that is fishing. world-view.ts places one sprite
 * at the boat on every draw and the swing carries the line with the hull.
 *
 * -- BENEATH THE HULL ---------------------------------------------------------------------------
 *
 * world-view.ts draws the visual in a layer of its own, above the water and the contact bands
 * and beneath every sprite that stands on the water. The line is on the water; where it meets
 * the hull it runs under the deck, so it leaves the silhouette's edge at any heading, and it
 * passes under an island rather than over the land.
 *
 * -- HARD PIXELS, THREE COLOURS -----------------------------------------------------------------
 *
 * The painter writes whole pixels at the authoring grid: ink for the resting line and the cork,
 * the water's own crest colour for an active catch (the design brief's one accent on water),
 * and the oxidized red for a beaten record. No alpha, no anti-aliasing, nothing between.
 */

import { CATCH_PHASE_END_MS, type CatchEvent, type CatchPhase } from '../app/catch-presentation';
import { STRIP_H } from './chart';
import { CHART_INK_RGB, CHART_PAPER_RGB, WATER_HIGHLIGHT_RGB, type Rgb } from './palette';
import type { RgbaImage } from './rotate';
import { ART_H, ART_W, type Px } from './scale';

export interface FishingVisualInput {
  readonly phase: CatchPhase;
  /** Milliseconds into `phase`, from the ceremony's own clock (catch-instrument.ts `catchPhaseMs`). */
  readonly phaseMs: number;
  /** The open water's loop frame, 0..15: the bobber rides it at rest. */
  readonly waterFrame: number;
  readonly reducedMotion: boolean;
  /** The catch under way, or null between catches. Decides the accent. */
  readonly kind: CatchEvent['kind'] | null;
  /** Where the plate takes the line during the reveal, from the boat, or null while no plate is up. */
  readonly plateFromBoatPx: Px | null;
}

export interface FishingVisual {
  readonly phase: CatchPhase;
  /** Where the line leaves the hull, from the boat's centre. */
  readonly lineFromBoatPx: Px;
  /** Where the line ends: the bobber, the hull during the pull, the plate during the reveal. */
  readonly bobberOffsetPx: Px;
  /** 0..1: the bite ring's growth. 0 means no ring. */
  readonly biteRing: number;
  /** 0..1: how far the bobber has been hauled to the hull. */
  readonly pull: number;
  readonly accent: 'none' | 'cyan' | 'record-red';
}

/**
 * The line leaves the hull to starboard, just aft of the bow. This point is UNDER the deck:
 * world-view.ts draws the whole visual beneath the boat, so the line shows from the gunwale
 * outward and the cork, hauled here at the end of the pull, goes aboard out of sight.
 */
export const LINE_HULL_PX: Px = { x: 16, y: -4 };
/**
 * During the reveal the line runs to the plate from the boat's own centre, which the hull
 * covers at every heading; drawn beneath the boat it appears at the silhouette's edge facing
 * the plate and never crosses the deck. (Fix round 1: a start 22 px down was called the hull's
 * foot, but the baked hull is 114 px long and centred on its anchor, so the foot is 57 px
 * down, and with the sprite then over the boat the crest line was painted across the stern.)
 */
export const REVEAL_UNDER_HULL_PX: Px = { x: 0, y: 0 };
/** The bobber rests 62 px out to starboard, a little aft, clear of the catch mark's place up ahead. */
export const BOBBER_REST_PX: Px = { x: 78, y: 4 };
export const LINE_OUT_MS = CATCH_PHASE_END_MS['line-out'];
export const BITE_MS = CATCH_PHASE_END_MS.bite - CATCH_PHASE_END_MS['line-out'];
export const PULL_MS = CATCH_PHASE_END_MS.pull - CATCH_PHASE_END_MS.bite;
/** After the reveal the line is cast again: the bobber flies from the hull back to its rest. */
export const RECAST_MS = 400;
/** The line goes taut: how far the bobber is tugged toward the hull, and how far down. */
const TUG_PX: Px = { x: -3, y: 2 };
const RING_MIN_R = 3;
const RING_MAX_R = 11;

/** The plate's authored width, in art px; its DOM footprint is this many grain px. */
export const PLATE_W = 320;
/** The ink stem between the plate's foot and the strip's top edge. */
export const PLATE_STEM_PX = 10;
/** The plate's foot sits one stem above the strip. */
export const PLATE_FOOT_Y = ART_H - STRIP_H - PLATE_STEM_PX;
/**
 * WHERE THE PLATE STANDS: BOTTOM-CENTRE, THE DESIGN BRIEF'S HOME, UNLESS THE HULL IS THERE.
 *
 * Two columns and one rule, measured rather than judged. The baked hull is 51 x 114, opaque
 * to its ends and centred on the anchor it is rotated about, so it reaches `HULL_REACH_PX`
 * from the boat's centre at the worst heading: in open water, with the boat at the rest row
 * (y 270), the stern is at y 328 at most, and the plate's foot is one stem above the strip
 * (y 466). Between the two is `plateCentreRoomPx`: 134 px with a 4 px clearance. A plate no
 * taller than that stands bottom-centre, over the water under the stern. A taller one, or
 * any plate while the arrival camera has the boat below the rest row (it moves the boat
 * down-screen only, and a mooring holds it 130 px down for minutes: nothing fits under a
 * moored hull), stands one column left in the pause sheet's column (x 96 to 416), which the
 * rotated hull never reaches (it stops at x 422) wherever the camera puts the boat.
 *
 * Fix round 1 had put the plate in the left column for good, because the full reveal plate
 * (158 px then) could not stand under the stern; fix round 2 restores the brief's home for
 * every plate that fits it - the Reel prompt, the compact strip and the everyday full plate,
 * now that its head carries the water's own mark at the water's own scale - and keeps the
 * left column for the rest. game-ui.ts measures the plate off the DOM and asks once per
 * ceremony, for the tallest form the ceremony will show, so a plate never moves between the
 * prompt and the reveal. fishing.test.ts rotates the baked hull through every heading
 * against both columns.
 */
export const PLATE_CENTRE_CX = ART_W / 2;
/** The pause sheet's column: x 96 to 416. */
export const PLATE_ASIDE_CX = 256;
/** The row the boat's centre sits on when the camera is on it (interpolate.ts `restY`). */
export const PLATE_REST_Y = ART_H / 2;
/** The furthest opaque pixel of the rotated hull from its anchor at any heading, in art px. Measured in fishing.test.ts. */
export const HULL_REACH_PX = 58;
/** Water between the stern and the plate's torn top. */
export const PLATE_HULL_CLEARANCE_PX = 4;

export const RECORD_RGB: Rgb = [101, 44, 48];

const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t);
const lerp = (a: Px, b: Px, t: number): Px => ({ x: Math.round(a.x + (b.x - a.x) * t), y: Math.round(a.y + (b.y - a.y) * t) });
const add = (a: Px, b: Px): Px => ({ x: a.x + b.x, y: a.y + b.y });

function accentFor(kind: CatchEvent['kind'] | null): FishingVisual['accent'] {
  // A first fish has no earlier best to beat: it is a new species, not a record, so no red.
  return kind === null ? 'none' : kind === 'record' ? 'record-red' : 'cyan';
}

export function fishingVisualAt(input: FishingVisualInput): FishingVisual {
  const { phase, reducedMotion } = input;
  // Reduced motion holds the frame each phase ends on; full motion moves through it.
  const t = reducedMotion ? 1 : clamp01(input.phaseMs / (phase === 'line-out' ? LINE_OUT_MS : phase === 'bite' ? BITE_MS : phase === 'pull' ? PULL_MS : RECAST_MS));
  const accent = accentFor(input.kind);
  const base = { phase, lineFromBoatPx: LINE_HULL_PX };
  switch (phase) {
    case 'line-out':
      return { ...base, bobberOffsetPx: reducedMotion ? BOBBER_REST_PX : lerp(BOBBER_REST_PX, add(BOBBER_REST_PX, TUG_PX), t), biteRing: 0, pull: 0, accent: 'none' };
    case 'bite':
      return { ...base, bobberOffsetPx: add(BOBBER_REST_PX, TUG_PX), biteRing: t, pull: 0, accent };
    case 'pull':
      return { ...base, bobberOffsetPx: lerp(add(BOBBER_REST_PX, TUG_PX), LINE_HULL_PX, t), biteRing: 0, pull: t, accent };
    case 'reel-window':
      return { ...base, bobberOffsetPx: LINE_HULL_PX, biteRing: 0, pull: 1, accent };
    case 'reveal':
      return input.plateFromBoatPx === null
        ? { ...base, bobberOffsetPx: LINE_HULL_PX, biteRing: 0, pull: 1, accent }
        : { phase, lineFromBoatPx: REVEAL_UNDER_HULL_PX, bobberOffsetPx: input.plateFromBoatPx, biteRing: 0, pull: 1, accent };
    default: {
      // Settled. After a catch the line is recast from the hull; then it rests and rides the water.
      const recasting = input.kind !== null && !reducedMotion && input.phaseMs < RECAST_MS;
      if (recasting) return { ...base, bobberOffsetPx: lerp(LINE_HULL_PX, BOBBER_REST_PX, t), biteRing: 0, pull: 0, accent: 'none' };
      const bob = !reducedMotion && input.waterFrame >= 8 ? 1 : 0;
      return { ...base, bobberOffsetPx: { x: BOBBER_REST_PX.x, y: BOBBER_REST_PX.y + bob }, biteRing: 0, pull: 0, accent: 'none' };
    }
  }
}

export interface PlatePlacementInput {
  /** The boat's centre down the frame, in art px: the rest row in open water, lower at a mooring. */
  readonly boatScreenY: number;
  /** The tallest the plate will be during this ceremony, in art px, measured off the DOM. */
  readonly plateHeightPx: number;
  /**
   * THE ART VIEWPORT (CONTRACT 1.5ac), default the authored 960x540. The two columns and the foot
   * are the same SHAPE on any view: the centre is the hull's own column `floor(w / 2)`, the aside is
   * the same 224 art px left of it, the rest row is `floor(h / 2)` and the foot one stem above the
   * strip from the view's bottom. At 960x540 those are 480, 256, 270 and 466, the numbers above.
   */
  readonly viewW?: number | undefined;
  readonly viewH?: number | undefined;
}
export interface PlatePlacement {
  readonly cx: number;
  readonly footY: number;
  readonly home: 'centre' | 'aside';
}

/** How tall a plate can stand bottom-centre with the boat's centre at `boatScreenY`, in art px. */
export function plateCentreRoomPx(boatScreenY: number, footY = PLATE_FOOT_Y): number {
  return footY - (boatScreenY + HULL_REACH_PX + PLATE_HULL_CLEARANCE_PX);
}

/** WHERE THE PLATE STANDS, in art px. See `PLATE_CENTRE_CX`. */
export function platePlacement(input: PlatePlacementInput): PlatePlacement {
  const viewW = input.viewW ?? ART_W;
  const viewH = input.viewH ?? ART_H;
  const centreCx = Math.floor(viewW / 2);
  const asideCx = centreCx - (PLATE_CENTRE_CX - PLATE_ASIDE_CX);
  const restY = Math.floor(viewH / 2);
  const footY = viewH - STRIP_H - PLATE_STEM_PX;
  const centred = input.boatScreenY <= restY && input.plateHeightPx <= plateCentreRoomPx(input.boatScreenY, footY);
  return { cx: centred ? centreCx : asideCx, footY, home: centred ? 'centre' : 'aside' };
}

/** Bresenham: every whole pixel from `a` to `b`, both ends included, 8-connected. */
export function linePixels(a: Px, b: Px): Px[] {
  const out: Px[] = [];
  let x = a.x;
  let y = a.y;
  const dx = Math.abs(b.x - a.x);
  const dy = -Math.abs(b.y - a.y);
  const sx = a.x < b.x ? 1 : -1;
  const sy = a.y < b.y ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    out.push({ x, y });
    if (x === b.x && y === b.y) return out;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x += sx; }
    if (e2 <= dx) { err += dx; y += sy; }
  }
}

/** Midpoint circle: the unique whole pixels of a ring of radius `r` about the origin. */
export function ringPixels(r: number): Px[] {
  const radius = Math.round(r);
  if (radius <= 0) return [];
  const seen = new Set<string>();
  const out: Px[] = [];
  const put = (x: number, y: number): void => {
    const k = `${x},${y}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ x, y });
  };
  let x = radius;
  let y = 0;
  let err = 1 - radius;
  while (x >= y) {
    for (const [px, py] of [[x, y], [y, x], [-y, x], [-x, y], [-x, -y], [-y, -x], [y, -x], [x, -y]] as const) put(px, py);
    y++;
    if (err < 0) err += 2 * y + 1;
    else { x--; err += 2 * (y - x) + 1; }
  }
  return out;
}

export interface FishingImage {
  readonly img: RgbaImage;
  /** Screen art px from the boat's centre to the image's top-left. */
  readonly dx: number;
  readonly dy: number;
}

/** The visual as whole pixels in three colours, boxed tight, placed from the boat. */
export function paintFishing(v: FishingVisual): FishingImage | null {
  const stroke: Rgb = v.accent === 'none' ? CHART_INK_RGB : v.accent === 'cyan' ? WATER_HIGHLIGHT_RGB : RECORD_RGB;
  const pixels: { p: Px; c: Rgb }[] = [];
  // The line: ink at rest, the crest colour for an active catch. Solid either way.
  for (const p of linePixels(v.lineFromBoatPx, v.bobberOffsetPx)) pixels.push({ p, c: v.accent === 'none' ? CHART_INK_RGB : WATER_HIGHLIGHT_RGB });
  // The bite ring, growing about the cork.
  if (v.biteRing > 0) {
    const r = Math.round(RING_MIN_R + (RING_MAX_R - RING_MIN_R) * clamp01(v.biteRing));
    for (const p of ringPixels(r)) pixels.push({ p: add(v.bobberOffsetPx, p), c: stroke });
  }
  // The cork: a 4x4 block, outlined in the stroke colour round a paper core. Not during the
  // reveal, when the line runs into the plate and the fish is on the paper.
  if (v.phase !== 'reveal') {
    for (let y = -2; y <= 1; y++) {
      for (let x = -2; x <= 1; x++) {
        const edge = x === -2 || x === 1 || y === -2 || y === 1;
        pixels.push({ p: add(v.bobberOffsetPx, { x, y }), c: edge ? stroke : CHART_PAPER_RGB });
      }
    }
  }
  if (pixels.length === 0) return null;
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const { p } of pixels) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); }
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const rgba = new Uint8ClampedArray(w * h * 4);
  // Later pixels win, so the cork's core lands over the line that runs into it.
  for (const { p, c } of pixels) {
    const i = ((p.y - minY) * w + (p.x - minX)) * 4;
    rgba[i] = c[0]; rgba[i + 1] = c[1]; rgba[i + 2] = c[2]; rgba[i + 3] = 255;
  }
  return { img: { w, h, rgba }, dx: minX, dy: minY };
}
